/**
 * Desktop refresh handler (no UI).
 *
 * A Tauri webview has no browser chrome, so there's no reload button. The
 * native View menu emits two events this component handles:
 *   - menu://refresh (Cmd+R)       — soft refresh: invalidate React Query so
 *     active screens re-fetch, WITHOUT reloading the SPA (session/auth survive,
 *     safe while offline — reads come from the offline cache). Also dispatches a
 *     window 'app:refresh' event so non-React-Query screens can opt in.
 *   - menu://reload (Cmd+Shift+R)  — hard reload of the webview.
 *
 * With messaging pop-outs there can be several windows, and Tauri delivers a
 * menu event to all of them. The shell sends the label of the focused window
 * as the payload, so only that window acts. A null payload (older shell)
 * still means "everyone".
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { currentDesktopWindowLabel } from '@/lib/desktopWindow';

function isForThisWindow(payload: unknown): boolean {
  if (typeof payload !== 'string' || !payload) return true;
  const mine = currentDesktopWindowLabel();
  return mine === null || mine === payload;
}

export function DesktopRefreshHandler() {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (typeof window === 'undefined' || !('__TAURI__' in window)) return;
    let unlistenRefresh: (() => void) | undefined;
    let unlistenReload: (() => void) | undefined;

    import('@tauri-apps/api/event')
      .then(async ({ listen }) => {
        unlistenRefresh = await listen('menu://refresh', (e) => {
          if (!isForThisWindow(e.payload)) return;
          // Refetch everything currently mounted; non-RQ screens can listen too.
          queryClient.invalidateQueries();
          window.dispatchEvent(new CustomEvent('app:refresh'));
        });
        unlistenReload = await listen('menu://reload', (e) => {
          if (!isForThisWindow(e.payload)) return;
          window.location.reload();
        });
      })
      .catch(() => {
        /* not in Tauri or event API unavailable */
      });

    return () => {
      unlistenRefresh?.();
      unlistenReload?.();
    };
  }, [queryClient]);

  return null;
}
